import React from 'react';
import { ArrowRight, CheckCircle, Target, Users, Award, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const CompanyProfile = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Company Profile | Alden Technologies — Turnkey Beverage Plant Experts"
        description="Learn about Alden Technologies — our mission, in-house manufacturing facility, engineering expertise and why 200+ clients trust us for turnkey water and beverage plants."
        path="/company-profile"
      />
      <Breadcrumbs items={[{ label: 'Company Profile' }]} />
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1605673393926-d95a8995e3de"
            alt="Company Profile"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About Alden Technologies
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Engineering Excellence in Beverage Industry Solutions Since 2009
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Who <span className="text-emerald-600">We Are</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Alden Technologies is a trusted name in the beverage industry turnkey solutions, delivering high-performance plants for packaged drinking water, carbonated soft drinks, and fruit juice processing.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With over 15 years of engineering excellence, we have successfully installed more than 200 beverage plants across India and neighboring countries. Our expertise spans across complete plant design, manufacturing of processing equipment, installation, automation, and comprehensive after-sales support.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our turnkey approach ensures a hassle-free experience with a single point of responsibility, making us the preferred partner for entrepreneurs and established businesses looking to enter or expand in the beverage manufacturing sector.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1530037335614-e68828dcf258"
                alt="Beverage Plant"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 border-emerald-200 bg-white">
              <CardContent className="p-10">
                <Target className="w-16 h-16 text-emerald-600 mb-6" />
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To empower entrepreneurs and businesses with world-class, cost-effective turnkey beverage plant solutions that combine cutting-edge technology, superior engineering, and exceptional service to ensure sustainable growth and operational excellence.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-200 bg-white">
              <CardContent className="p-10">
                <TrendingUp className="w-16 h-16 text-emerald-600 mb-6" />
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To be recognized as the most trusted and innovative turnkey project provider in the beverage manufacturing industry across Asia, setting benchmarks in quality, efficiency, and customer satisfaction while contributing to the growth of the food and beverage sector.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core <span className="text-emerald-600">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that drive our commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                id: "quality",
                icon: Award,
                title: "Quality Excellence",
                description: "Uncompromising commitment to delivering superior quality in every project component"
              },
              {
                id: "customer",
                icon: Users,
                title: "Customer First",
                description: "Building lasting relationships through exceptional service and support"
              },
              {
                id: "innovation",
                icon: CheckCircle,
                title: "Innovation",
                description: "Continuously adopting latest technologies and engineering practices"
              },
              {
                id: "integrity",
                icon: Target,
                title: "Integrity",
                description: "Transparent operations with ethical business practices and honest communication"
              }
            ].map((value) => (
              <Card key={value.id} className="text-center border-2 border-gray-200 hover:border-emerald-500 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-emerald-600">Alden Technologies?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our competitive advantages in the beverage plant industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { id: "turnkey", text: "Complete Turnkey Project Execution - Single Point Responsibility" },
              { id: "custom", text: "Customized Plant Design Based on Your Requirements" },
              { id: "inhouse", text: "In-house Manufacturing of Critical Equipment" },
              { id: "fast", text: "Fast Installation & Commissioning (3-6 Months)" },
              { id: "plc", text: "PLC-Based Automation for Efficient Operations" },
              { id: "training", text: "Comprehensive Operator Training Programs" },
              { id: "support", text: "24/7 After-Sales Technical Support" },
              { id: "compliance", text: "Complete Compliance Assistance (BIS, FSSAI)" },
              { id: "efficient", text: "Energy-Efficient & Cost-Effective Solutions" },
              { id: "track", text: "Proven Track Record with 200+ Installations" }
            ].map((item) => (
              <div key={item.id} className="flex items-start gap-4 p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                <span className="text-lg text-gray-800">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-emerald-600">Expertise</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-emerald-600 mb-4">Engineering & Design</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Process flow design</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Plant layout planning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Capacity optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>3D modeling & simulation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-emerald-600 mb-4">Manufacturing</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>SS 304/316 equipment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Quality raw materials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Precision fabrication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Rigorous quality checks</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-emerald-600 mb-4">Support Services</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Installation & commissioning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Operator training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Spare parts supply</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>24/7 technical support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Beverage Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Partner with industry experts for your turnkey beverage plant project
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompanyProfile;
